import { useState } from "react";
import styles from "./orders.module.css";

import {
  Image,
  Button,
  Pagination,
  Loader,
} from "../../../../components/shared/index";

import {
  useCancelOrderMutation,
  useGetOrdersQuery,
} from "../../../../queries/useOrderQuery";

import { useAddToCart } from "../../../../queries/useCartQuery";

import Swal from "sweetalert2";
import toast from "react-hot-toast";

import { getImageUrl } from "../../../../lib/get-image";
import { Link, useNavigate } from "react-router-dom";

import { FiDownload } from "react-icons/fi";
import { TbInvoice } from "react-icons/tb";

const Orders = () => {
  const navigate = useNavigate();

  const { data, isPending, error } = useGetOrdersQuery();

  const { mutate: addToCart } = useAddToCart();
  const cancelMutation = useCancelOrderMutation();

  const [addingId, setAddingId] = useState(null);
  const [cancellingOrderId, setCancellingOrderId] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);

  const per_page = 6;

  const userOrdersHistory = Array.isArray(data)
    ? data
    : data?.orders || [];

  if (isPending) {
    return <Loader title="Loading Orders..." />;
  }

  if (error) {
    return <Loader title="Failed to load orders..." />;
  }

  if (!userOrdersHistory.length) {
    return (
      <div className={styles.emptyState}>
        <div className={styles.emptyIcon}>
          <TbInvoice size={55} />
        </div>

        <h2>No Orders Yet!</h2>

        <p>
          You haven't placed any orders yet. Start exploring our
          collection and find something you love!
        </p>

        <Button
          type="button"
          title="Continue Shopping"
          style={styles.continueShoppingBtn}
          onHandler={() => navigate("/shop")}
        />
      </div>
    );
  }

  const orders = userOrdersHistory.map((order) => {
    const products = order?.order_products || [];

    return {
      id: order?.id,
      invoiceId: order?.invoice_id,
      status: order?.order_status,
      amount: Number(order?.amount || 0),

      totalItems: products.reduce(
        (total, product) =>
          total + Number(product?.qty || 0),
        0
      ),

     products: products.map((item) => ({
  id: item?.id,

  productId: item?.product_id,

  // Required by the cart API
  productVariantItemId:
    item?.product_variant_item_id ||
    item?.variant_item?.id ||
    null,

  vendorId: item?.vendor_id,
  productSlug: item?.product?.slug,

  name:
    item?.product_name ||
    item?.product?.name ||
    "Product",

  image:
    item?.product?.thumb_image ||
    "/assets/img/backgrounds/not_found.png",

  quantity: Number(item?.qty || 1),
  price: Number(item?.unit_price || 0),

  sku:
    item?.variant_sku ||
    item?.variant_item?.variant_sku ||
    "-",

  // Variant size and unit
  variantSize:
    item?.variant_size ||
    item?.variant_item?.variant_size ||
    "",

  unit:
    item?.unit ||
    item?.variant_item?.unit ||
    "",
})),
    };
  });

  // Pagination: 6 orders per page
  const indexOfLast = currentPage * per_page;
  const indexOfFirst = indexOfLast - per_page;

  const currentOrders = orders.slice(
    indexOfFirst,
    indexOfLast
  );

  // Cancel order
  const handleCancelOrder = (order) => {
    Swal.fire({
      title: "Cancel this Order?",
      text: "Are you sure you want to cancel this order?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, Cancel",
      cancelButtonText: "No",
    }).then((result) => {
      if (result.isConfirmed) {
        setCancellingOrderId(order.id);

        cancelMutation.mutate(order.id, {
          onError: () => {
            toast.error("Failed to cancel order");
          },

          onSettled: () => {
            setCancellingOrderId(null);
          },
        });
      }
    });
  };

 // Buy Again
const handleAddToCart = (order) => {
  const products = order.products || [];

  if (!products.length) {
    toast.error("No products found in this order");
    return;
  }

  // Check whether every product has a valid variant ID
  const missingVariant = products.some(
    (product) => !product.productVariantItemId
  );

  if (missingVariant) {
    toast.error("Product variant information is missing");
    return;
  }

  setAddingId(order.id);

  let completed = 0;
  let failed = false;

  products.forEach((product) => {
    addToCart(
      {
        product_id: product.productId,
        product_variant_item_id:
          product.productVariantItemId,
        quantity: product.quantity,
      },
      {
        onSuccess: () => {
          completed++;

          if (completed === products.length) {
            setAddingId(null);

            if (!failed) {
              toast.success("All products added to cart");
            } else {
              toast.error(
                "Some products could not be added"
              );
            }
          }
        },

        onError: (error) => {
          failed = true;
          completed++;

          console.error(
            "Failed to add product to cart:",
            error
          );

          if (completed === products.length) {
            setAddingId(null);

            toast.error(
              "Some products could not be added"
            );
          }
        },
      }
    );
  });
};

  return (
    <section className={styles.wrapper}>
      <div className={styles.container}>
        <div className={styles.ordersGrid}>
          {currentOrders.map((order) => {
            const orderStatus =
              order.status?.toLowerCase() || "pending";

            return (
              <div
                key={order.id}
                className={styles.orderCard}
              >
                {/* Compact order header */}
                <div className={styles.orderHeader}>
                  <div className={styles.orderHeaderLeft}>
                    <h3 className={styles.invoiceId}>
                      Invoice: {order.invoiceId}
                    </h3>

                    <div className={styles.orderMeta}>
                      <span className={styles.orderTotal}>
                        Order Total: ₹{order.amount.toFixed(2)}
                      </span>

                      <span className={styles.metaDivider}>
                        |
                      </span>

                      <span className={styles.itemCount}>
                        Items: {order.totalItems}
                      </span>
                    </div>
                  </div>

                  <span
                    className={`${styles.statusBadge} ${
                      styles[orderStatus] || ""
                    }`}
                  >
                    {order.status?.replace(/_/g, " ") || "Pending"}
                  </span>
                </div>

                {/* Products */}
                <div className={styles.productsList}>
                  {order.products.map((product) => (
                    <div
                      key={product.id}
                      className={styles.productRow}
                    >
                      <Link
                        to={
                          product.productSlug
                            ? `/productdetails/${product.productSlug}`
                            : "/shop"
                        }
                        target="_blank"
                        rel="noopener noreferrer"
                        className={styles.imageWrapper}
                      >
                        <Image
                          src={getImageUrl(product.image)}
                          alt={product.name}
                          className={styles.productImage}
                        />
                      </Link>

                      <div className={styles.productDetails}>
                        <h4 className={styles.productName}>
                          {product.name}
                        </h4>

                        <span className={styles.salePrice}>
                          ₹{product.price.toFixed(2)}
                        </span>

                        <div className={styles.productMeta}>
                          <span>Qty: {product.quantity}</span>

                          <span>SKU: {product.sku}</span>

                          {product.unit && (
                            <span>{product.unit}</span>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Buy Again and Invoice */}
                <div className={styles.orderActions}>
                  <Button
                    type="button"
                    title={
                      addingId === order.id
                        ? "Adding..."
                        : "Buy Again"
                    }
                    style={styles.buyAgainButton}
                    disabled={addingId === order.id}
                    onHandler={() => handleAddToCart(order)}
                  />

                  <Button
                    type="button"
                    title={
                      <span className={styles.invoiceButtonContent}>
                        <FiDownload size={16} />
                        Invoice
                      </span>
                    }
                    style={styles.invoiceButton}
                    onHandler={() =>
                      navigate(`/invoice/${order.invoiceId}`)
                    }
                  />
                </div>

                {/* Cancel order */}
                {orderStatus !== "cancelled" &&
                  orderStatus !== "delivered" && (
                    <Button
                      type="button"
                      title="Cancel Order"
                      style={styles.cancelButton}
                      isLoading={
                        cancellingOrderId === order.id &&
                        cancelMutation.isPending
                      }
                      disabled={
                        cancellingOrderId === order.id &&
                        cancelMutation.isPending
                      }
                      onHandler={() =>
                        handleCancelOrder(order)
                      }
                    />
                  )}

                {orderStatus === "cancelled" && (
                  <div className={styles.cancelledBadge}>
                    Order Cancelled
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Pagination - preserved */}
        {orders.length > per_page && (
          <div className={styles.paginationWrapper}>
            <Pagination
              currentPage={currentPage}
              total={orders.length}
              per_page={per_page}
              handlePageChange={setCurrentPage}
            />
          </div>
        )}
      </div>
    </section>
  );
};

export default Orders;